/**
 * Nuclia RAO Standalone Service
 * Dependencies: npm install @nuclia/core rxjs
 */

import { Nuclia, type AragAnswer, type AragResponse, type IErrorResponse } from '@nuclia/core';
import { switchMap, type Observable, Subscription } from 'rxjs';
import { Visualization } from '../lib/rao-types';

export type NucliaConfig = {
    accountId: string;
    backend: string;
    zone: string;
    knowledgeBox: string;
    apiKey: string;
}

export interface AskQuestionOptions {
    query: string;
    documentName?: string;
    enableInternetSearch?: boolean;
    enableRestrictedInternal?: boolean;
    conversationId?: string;
}

export interface RaoStep {
    module?: string | null;
    title?: string | null;
    value?: string | null;
    reason?: string | null;
    agentPath?: string | null;
}

export interface RaoChunk {
    chunkId: string;
    text: string;
    title?: string | null;
    source?: string | null;
    url?: string[] | null;
    originUrl?: string | null;
}

export interface RaoSourceContext {
    contextId?: string;
    agentId: string;
    source: string;
    title: string;
    summary?: string;
    chunks: RaoChunk[];
    citations: string[];
}

export interface RaoResult {
    answer: string;
    steps: RaoStep[];
    citations: string[];
    chunks: RaoChunk[];
    source?: string;
    sourceContexts: RaoSourceContext[];
    success: boolean;
    error?: string;
    dataVisualizations?: Visualization[];
    answerUrls?: string[];
    tokenCounts?: { inputTokens: number; outputTokens: number; totalTokens: number };
    citationMetadata?: Record<string, { contextId: string; originUrls: string[] }>;
}

export interface StreamCallbacks {
    onTextUpdate?: (text: string) => void;
    onStep?: (step: RaoStep) => void;
    onSourceContext?: (context: RaoSourceContext) => void;
    onComplete?: (result: RaoResult) => void;
    onError?: (error: string) => void;
    onReceiveVisualizations?: (spec: Visualization[]) => void;
}

export function generateConversationId(): string {
    const h = () => Math.floor(Math.random() * 16).toString(16);
    const s = () => h() + h() + h() + h();
    return `${s()}${s()}-${s()}-4${h()}${h()}${h()}-${['8', '9', 'a', 'b'][Math.floor(Math.random() * 4)]}${h()}${h()}${h()}-${s()}${s()}${s()}`;
}

function isErrorResponse(value: unknown): value is IErrorResponse {
    return !!value && typeof value === 'object' && (value as IErrorResponse).type === 'error';
}

function isStreamComplete(answer: AragAnswer): boolean {
    const obj = answer as unknown as Record<string, unknown>;
    return typeof obj.operation === 'number' && (obj.operation === 3 || obj.operation === 4);
}

function extractText(payload: AragResponse): string | null {
    if (payload.type !== 'answer') return null;
    const answer = payload.answer as unknown as Record<string, unknown> | undefined;
    if (!answer) return null;
    if (typeof answer.generated_text === 'string' && answer.generated_text.trim()) return answer.generated_text;
    if (typeof answer.answer === 'string' && answer.answer.trim()) return answer.answer;
    return null;
}

function extractDataVisualizations(payload: AragResponse): Visualization[] | null {
    if (payload.type !== 'answer') return null;
    const answer = payload.answer as unknown as Record<string, unknown> | undefined;
    if (!answer) return null;
    const dvs = answer.data_visualizations as Visualization[] | undefined;
    if (!dvs || !Array.isArray(dvs) || dvs.length === 0) return null;
    // Filter out visualizations without valid vega_lite_obj
    const validVisualizations = dvs.filter(v => v && v.vega_lite_obj);
    return validVisualizations.length > 0 ? validVisualizations : null;
}

function extractAnswerUrls(payload: AragResponse): string[] | null {
    if (payload.type !== 'answer') return null;
    const answer = payload.answer as unknown as Record<string, unknown> | undefined;
    if (!answer) return null;
    const urls = answer.answer_urls as unknown;
    if (!Array.isArray(urls) || urls.length === 0) return null;
    const validUrls = urls.filter((u): u is string => typeof u === 'string' && u.trim().length > 0);
    return validUrls.length > 0 ? validUrls : null;
}

function extractException(payload: AragResponse): string | null {
    if (payload.type !== 'answer') return null;
    const answer = payload.answer as unknown as Record<string, unknown> | undefined;
    if (!answer) return null;
    const exception = answer.exception as Record<string, unknown> | undefined;
    if (!exception) return null;
    // Try to get error message from exception object
    if (typeof exception.message === 'string') return exception.message;
    if (typeof exception.detail === 'string') return exception.detail;
    return String(exception);
}

function extractTokenCounts(payload: AragResponse): {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
} | null {
    if (payload.type !== 'answer') return null;
    const answer = payload.answer as unknown as Record<string, unknown> | undefined;
    if (!answer) return null;
    const step = answer.step as Record<string, unknown> | undefined;
    if (!step) return null;

    const inputTokens = typeof step.input_nuclia_tokens === 'number' ? step.input_nuclia_tokens : 0;
    const outputTokens = typeof step.output_nuclia_tokens === 'number' ? step.output_nuclia_tokens : 0;

    // Only return if we have at least some token data
    if (inputTokens === 0 && outputTokens === 0) return null;

    return {
        inputTokens,
        outputTokens,
        totalTokens: inputTokens + outputTokens,
    };
}

function extractAnswerCitations(payload: AragResponse): Record<string, { contextId: string; originUrls: string[] }> | null {
    if (payload.type !== 'answer') return null;
    const answer = payload.answer as unknown as Record<string, unknown> | undefined;
    if (!answer) return null;
    const answerCitations = answer.answer_citations as Record<string, unknown> | undefined;
    if (!answerCitations) return null;
    const metadata = answerCitations.metadata as Record<string, unknown> | undefined;
    if (!metadata) return null;

    const result: Record<string, { contextId: string; originUrls: string[] }> = {};
    
    for (const [blockId, blockData] of Object.entries(metadata)) {
        if (typeof blockData !== 'object' || blockData === null) continue;
        const data = blockData as Record<string, unknown>;
        const contextId = typeof data.context_id === 'string' ? data.context_id : '';
        const originUrls = Array.isArray(data.origin_urls) ? data.origin_urls.filter((u): u is string => typeof u === 'string') : [];
        
        if (contextId) {
            result[blockId] = { contextId, originUrls };
        }
    }

    return Object.keys(result).length > 0 ? result : null;
}

function extractStep(payload: AragResponse): RaoStep | null {
    if (payload.type !== 'answer') return null;
    const answer = payload.answer as unknown as Record<string, unknown> | undefined;
    if (!answer) return null;
    const step = answer.step as Record<string, unknown> | undefined;
    if (!step) return null;
    const { module, title, value, reason, agent_path } = step;
    if (!module && !title && !value) return null;
    return {
        module: typeof module === 'string' ? module : null,
        title: typeof title === 'string' ? title : null,
        value: typeof value === 'string' ? value : null,
        reason: typeof reason === 'string' ? reason : null,
        agentPath: typeof agent_path === 'string' ? agent_path : null,
    };
}

function extractContext(payload: AragResponse): RaoSourceContext | null {
    if (payload.type !== 'answer') return null;
    const answer = payload.answer as unknown as Record<string, unknown> | undefined;
    if (!answer) return null;
    const context = answer.context as Record<string, unknown> | undefined;
    if (!context) return null;

    const contextId = typeof context.id === 'string' ? context.id : undefined;
    const agentId = typeof context.agent_id === 'string' ? context.agent_id : '';
    const source = typeof context.source === 'string' ? context.source : '';
    const title = typeof context.title === 'string' ? context.title : '';
    const summary = typeof context.summary === 'string' ? context.summary : undefined;

    // Skip if no agent_id and no source - not a valid source context
    if (!agentId && !source) return null;

    const citations = Array.isArray(context.citations)
        ? context.citations.filter((c): c is string => typeof c === 'string')
        : [];

    const chunks: RaoChunk[] = [];

    if (Array.isArray(context.chunks)) {
        for (const chunk of context.chunks) {
            if (chunk && typeof chunk === 'object') {
                const c = chunk as Record<string, unknown>;
                const urlArray = Array.isArray(c.url)
                    ? c.url.filter((u): u is string => typeof u === 'string')
                    : null;
                const originUrl = typeof c.origin_url === 'string' ? c.origin_url : null;
                chunks.push({
                    chunkId: typeof c.chunk_id === 'string' ? c.chunk_id : '',
                    text: typeof c.text === 'string' ? c.text : '',
                    title: typeof c.title === 'string' ? c.title : null,
                    source: typeof c.source === 'string' ? c.source : null,
                    url: urlArray,
                    originUrl: originUrl,
                });
            }
        }
    }

    return { contextId, agentId, source, title, summary, chunks, citations };
}

function mergeText(prev: string, next: string): string {
    if (!prev) return next;
    if (!next) return prev;
    if (next === prev) return prev;
    if (next.startsWith(prev)) return next;
    if (prev.startsWith(next)) return prev;
    return `${prev}\n${next}`;
}

let nucliaConfigCache: NucliaConfig | null = null;

export async function fetchNucliaConfig(): Promise<NucliaConfig> {
    if (nucliaConfigCache)
        return nucliaConfigCache;
    const resp = await fetch("/api/config", { credentials: 'include' });

    if (!resp.ok)
        throw new Error(`Failed to load config: ${resp.status}`);

    const json = await resp.json();
    // To prvent multiple trips to the config API, these values wont change frequently
    nucliaConfigCache = {
        accountId: json.accountId,
        backend: json.backend,
        zone: json.zone,
        knowledgeBox: json.KnowledgeBox,
        apiKey: json.apiKey
    };

    return nucliaConfigCache;
}

export class NucliaService {
    private client: Nuclia;
    private subscription: Subscription | null = null;

    constructor(config: NucliaConfig) {
        this.client = new Nuclia({
            accountId: config.accountId,
            backend: config.backend,
            zone: config.zone,
            knowledgeBox: config.knowledgeBox,
            apiKey: config.apiKey,
        });
    }

    async askQuestion(options: AskQuestionOptions, callbacks?: StreamCallbacks): Promise<RaoResult> {
        const { query, conversationId = generateConversationId() } = options;
        const headers: Record<string, string> = {};
        if (options.documentName) headers['X-RESTRICTED-DATASET'] = options.documentName;
        headers['X-RESTRICTED-INTERNET'] = options.enableInternetSearch ? 'true' : 'false';
        headers['X-RESTRICTED-INTERNAL'] = options.enableRestrictedInternal ? 'true' : 'false';

        console.log('Query:', query);
        console.log('Document:', options.documentName || '(none)');
        console.log('Internet:', options.enableInternetSearch ? 'on' : 'off');

        return new Promise((resolve) => {
            let text = '';
            const steps: RaoStep[] = [];
            const sourceContexts: RaoSourceContext[] = [];
            let error = '';
            const allDataVisualizations: Visualization[] = [];
            const allAnswerUrls: string[] = [];
            let totalInputTokens = 0;
            let totalOutputTokens = 0;
            let citationMetadata: Record<string, { contextId: string; originUrls: string[] }> | undefined;
            const source$: Observable<AragResponse | IErrorResponse> = this.client.db
                .getRetrievalAgent()
                .pipe(switchMap((agent) => agent.interact(conversationId, query, 'WS', headers)));

            this.cancel();

            const done = (success: boolean) => {
                const allChunks = sourceContexts.flatMap(ctx => ctx.chunks);
                const allCitations = sourceContexts.flatMap(ctx => ctx.citations);
                const lastSource = sourceContexts.length > 0 ? sourceContexts[sourceContexts.length - 1].source : undefined;

                const tokenCounts = (totalInputTokens > 0 || totalOutputTokens > 0) ? {
                    inputTokens: totalInputTokens,
                    outputTokens: totalOutputTokens,
                    totalTokens: totalInputTokens + totalOutputTokens
                } : undefined;

                const result: RaoResult = {
                    answer: text,
                    steps,
                    citations: allCitations,
                    chunks: allChunks,
                    source: lastSource,
                    sourceContexts,
                    success,
                    error: error || undefined,
                    dataVisualizations: allDataVisualizations,
                    answerUrls: allAnswerUrls.length > 0 ? allAnswerUrls : undefined,
                    tokenCounts,
                    citationMetadata
                };
                callbacks?.onComplete?.(result);
                console.log('\n--- RESULT ---');
                console.log('Success:', success);
                if (error) console.log('Service Error:', error);
                console.log('Answer:', text || '(none)');
                console.log('Steps:', steps.length, 'SourceContexts:', sourceContexts.length, 'Chunks:', allChunks.length);
                resolve(result);
            };

            this.subscription = source$.subscribe({
                next: (res) => {
                    if (isErrorResponse(res)) {
                        error = res.detail || `HTTP ${res.status}`;
                        console.error('Service Error:', error);
                        callbacks?.onError?.(error);
                        return;
                    }

                    const r = res as AragResponse;
                    console.log('[ARAG] Response:', r);

                    const exception = extractException(r);
                    if (exception) {
                        error = exception;
                        console.error('Exception in answer:', error);
                        callbacks?.onError?.(error);
                        done(false);
                        return;
                    }

                    const t = extractText(r);
                    if (t) { text = mergeText(text, t); callbacks?.onTextUpdate?.(text); }

                    const s = extractStep(r);
                    if (s) { steps.push(s); callbacks?.onStep?.(s); }

                    const dataVisualizations = extractDataVisualizations(r);
                    if (dataVisualizations) { 
                        allDataVisualizations.push(...dataVisualizations);

                        // Not needed because we're not rendering charts in the stream, only in the final result
                        // This is left in for debugging purposes and possible future use.
                        callbacks?.onReceiveVisualizations?.(dataVisualizations); 
                    }

                    const answerUrls = extractAnswerUrls(r);
                    if (answerUrls) {
                        allAnswerUrls.push(...answerUrls);
                    }

                    const tokenCounts = extractTokenCounts(r);
                    if (tokenCounts) {
                        totalInputTokens += tokenCounts.inputTokens;
                        totalOutputTokens += tokenCounts.outputTokens;
                    }

                    const citations = extractAnswerCitations(r);
                    if (citations) {
                        citationMetadata = citations;
                        console.log('[ARAG] Citation metadata:', citations);
                    }

                    const ctx = extractContext(r);
                    if (ctx) {
                        sourceContexts.push(ctx);
                        callbacks?.onSourceContext?.(ctx);
                        console.log('[ARAG] Source context:', ctx);
                    }

                    if (r.type === 'answer' && isStreamComplete(r.answer)) done(!error);
                },
                error: (e) => { error = e instanceof Error ? e.message : 'Error'; callbacks?.onError?.(error); done(false); },
                complete: () => { if (!error) done(true); },
            });
        });
    }

    cancel(): void {
        this.subscription?.unsubscribe();
        this.subscription = null;
    }
}

export async function createNucliaService(config?: Partial<NucliaConfig>): Promise<NucliaService> {
    try {
        const cfg = await fetchNucliaConfig();

        await new Promise(resolve => setTimeout(resolve, 2000)); // simulate async delay
        return new NucliaService({ ...cfg, ...config });
    } catch (error) {
        console.error('Error fetching Nuclia config:', error);
        throw new Error('Failed to initialize Nuclia service');
    }
}
